/**
 * 剧本配置块类型定义
 * 对应后端 ScriptConfigBlock 模型和 Pydantic Schemas
 */

// 配置类型枚举
export type ConfigType =
  | 'npc'           // NPC配置
  | 'quest'         // 任务配置
  | 'item'          // 物品配置
  | 'variable'      // 变量配置
  | 'timeline'      // 时间线配置
  | 'world'         // 世界配置
  | 'faction'       // 派系配置
  | 'location'      // 地点配置
  | 'action'        // 动作配置
  | 'event'         // 事件配置
  | 'dialogue'      // 对话配置
  | 'ai_model'      // AI模型配置
  | 'resource_image' // 资源图片配置（NPC头像、场景背景等）
  // AI Engine V2.1 核心配置文件
  | 'v21_script'    // V2.1脚本配置 (script.yaml)
  | 'v21_world'     // V2.1世界配置 (world.yaml)
  | 'v21_timeline'  // V2.1时间线配置 (timeline.yaml)
  | 'v21_variables' // V2.1变量配置 (variables.yaml)

// 配置类型中文映射
export const CONFIG_TYPE_LABELS: Record<ConfigType, string> = {
  npc: 'NPC配置',
  quest: '任务配置',
  item: '物品配置',
  variable: '变量配置',
  timeline: '时间线配置',
  world: '世界配置',
  faction: '派系配置',
  location: '地点配置',
  action: '动作配置',
  event: '事件配置',
  dialogue: '对话配置',
  ai_model: 'AI模型配置',
  resource_image: '资源图片配置',
  // AI Engine V2.1 核心配置文件
  v21_script: 'V2.1脚本配置',
  v21_world: 'V2.1世界配置',
  v21_timeline: 'V2.1时间线配置',
  v21_variables: 'V2.1变量配置',
}

// 配置类型图标映射
export const CONFIG_TYPE_ICONS: Record<ConfigType, string> = {
  npc: 'Team',
  quest: 'Aim',
  item: 'ShoppingCart',
  variable: 'Database',
  timeline: 'ClockCircle',
  world: 'Global',
  faction: 'Flag',
  location: 'Environment',
  action: 'Thunderbolt',
  event: 'Bell',
  dialogue: 'Message',
  ai_model: 'Robot',
  resource_image: 'Picture', // 使用 Picture 图标表示资源图片
  // AI Engine V2.1 核心配置文件
  v21_script: 'FileText',    // 脚本配置使用文件图标
  v21_world: 'Global',       // 世界配置使用地球图标
  v21_timeline: 'ClockCircle', // 时间线配置使用时钟图标
  v21_variables: 'Database',   // 变量配置使用数据库图标
}

// 剧本配置块
export interface ScriptConfigBlock {
  id: number
  package_id: string           // 剧本包ID
  config_type: ConfigType      // 配置类型
  config_key: string           // 配置键
  config_name: string          // 配置名称
  config_data: Record<string, any>  // 配置数据
  version: number              // 版本号
  sort_order: number           // 排序权重
  is_active: boolean           // 是否激活
  created_at: string           // 创建时间
  updated_at: string           // 更新时间
  extra_metadata?: Record<string, any>  // 扩展元数据
}

// 创建配置块请求
export interface CreateConfigBlockRequest {
  package_id: string
  config_type: ConfigType
  config_key: string
  config_name: string
  config_data: Record<string, any>
  sort_order?: number
  extra_metadata?: Record<string, any>
}

// 更新配置块请求
export interface UpdateConfigBlockRequest {
  config_name?: string
  config_data?: Record<string, any>
  sort_order?: number
  is_active?: boolean
  extra_metadata?: Record<string, any>
}

// 配置块列表响应
export interface ConfigBlockListResponse {
  items: ScriptConfigBlock[]
  total: number
  page: number
  limit: number
  pages: number  // 总页数
}

// 按类型分组的配置块响应
export interface ConfigBlockByTypeResponse {
  config_type: ConfigType
  items: ScriptConfigBlock[]
  total: number
}

// 配置块统计响应（后端返回格式）
// 支持通过 package_id 或 script_id 查询
export interface ConfigBlockStatsResponse {
  package_id?: string  // 通过 package_id 查询时返回
  script_id?: number  // 通过 script_id 查询时返回
  stats: Record<string, number>  // 按类型统计的数量
  total: number  // 总数
  active_count?: number  // 额外计算：激活数量
  inactive_count?: number  // 额外计算：禁用数量
  latest_updated_at?: string | null  // 最新更新时间
}

// 导出响应
export interface ConfigBlockExportResponse {
  package_id: string
  config_blocks: Array<{
    config_type: string
    config_key: string
    config_name: string
    config_data: Record<string, any>
    sort_order: number
    version: number
    extra_metadata?: Record<string, any>
  }>
  total: number
}

// 导入响应
export interface ConfigBlockImportResponse {
  created_count: number
  errors: Array<{
    config_key: string
    error: string
  }>
}

// 批量创建响应
export interface ConfigBlockBatchCreateResponse {
  created: ScriptConfigBlock[]
  created_count: number
}

// 剧本包配置块响应（按包ID查询）
export interface ScriptPackageBlocksResponse {
  package_id: string
  package_name: string
  config_blocks: ConfigBlockByTypeResponse[]
}

// 删除配置块响应
export interface ConfigBlockDeleteResponse {
  deleted_count: number
}

// 剧本包配置概览
export interface ScriptPackageOverview {
  id: number
  package_id: string
  package_name: string
  version: string
  status: string
  config_block_count: number
  last_exported_at: string | null
}

// NPC 配置数据（导出格式）
// 注意：导出时会将 key 转换为 npc_id
export interface NpcConfigData {
  key: string                // 编辑时使用
  npc_id?: string            // 导出字段名，与 ai-engine 兼容
  name: string
  role: string
  description?: string       // ai-engine 格式
  avatar?: string            // ai-engine 格式
  gender?: string
  age?: string
  personality?: string
  personality_traits?: string[]  // ai-engine 格式
  speech_style?: string      // ai-engine 格式
  background?: string
  appearance?: string
  voice_style?: string
  initial_memory?: string
  system_prompt?: string
  ai_config?: {              // ai-engine 格式
    model?: string
    system_prompt?: string
    temperature?: number
    max_tokens?: number
  }
  ai_model_id?: string
  temperature?: number
  max_tokens?: number
  behaviors?: string[]
  dialogues?: DialogueEntry[]
  relations?: NpcRelation[]
  available_locations?: string[]
  schedule?: NpcSchedule
  states?: {                 // ai-engine 格式
    initial?: string
    [key: string]: string | undefined
  }
}

// 对话条目
export interface DialogueEntry {
  id: string
  trigger?: string
  lines: DialogueLine[]
  conditions?: Record<string, any>
}

// 对话线路
export interface DialogueLine {
  speaker: string
  content: string
  emotion?: string
  action?: string
}

// NPC 关系
export interface NpcRelation {
  target_npc_key: string
  relation_type: string
  initial_value: number
  description?: string
}

// NPC 日程
export interface NpcSchedule {
  [timeSlot: string]: {
    location: string
    activity: string
    conditions?: Record<string, any>
  }
}

// 任务配置数据（导出格式）
// 注意：导出时会将 key 转换为 quest_id
export interface QuestConfigData {
  key: string                // 编辑时使用
  quest_id?: string          // 导出字段名，与 ai-engine 兼容
  name: string
  description?: string
  quest_type: string         // main/branch/daily/event/hidden
  difficulty?: string
  prerequisites?: string[]
  objectives: QuestObjective[]
  rewards?: QuestReward[]
  time_limit?: number
  auto_advance?: boolean
  failure_conditions?: string[]
  success_conditions?: string[]
  // ai-engine 兼容字段
  type?: string              // ai-engine 使用 type
  prerequisites_quests?: string[]  // ai-engine 前置任务格式
  rewards_experience?: number      // ai-engine 奖励格式
  rewards_items?: string[]         // ai-engine 奖励格式
}

// 任务目标
export interface QuestObjective {
  id: string
  description: string
  type: string
  target_key?: string
  required_count?: number
  is_optional?: boolean
  hint?: string
}

// 任务奖励
export interface QuestReward {
  type: string
  key?: string
  value?: number | string
  description?: string
}

// 物品配置数据
export interface ItemConfigData {
  key: string
  name: string
  description: string
  item_type: string
  rarity?: string
  icon?: string
  value?: number
  weight?: number
  max_stack?: number
  properties?: Record<string, any>
  effects?: ItemEffect[]
  source?: string[]
  can_trade?: boolean
  can_drop?: boolean
}

// 物品效果
export interface ItemEffect {
  effect_type: string
  target?: string
  value?: number | string
  duration?: number
  description?: string
}

// 变量配置数据
export interface VariableConfigData {
  key: string
  name: string
  description?: string
  var_type: string
  initial_value?: any
  scope: 'global' | 'player' | 'faction'
  modifier?: string
  persist_between_sessions?: boolean
}

// 时间线配置数据
export interface TimelineConfigData {
  key: string
  name: string
  description?: string
  events: TimelineEvent[]
  start_conditions?: Record<string, any>
  end_conditions?: Record<string, any>
  is_cyclic?: boolean
}

// 时间线事件
export interface TimelineEvent {
  id: string
  time_offset: number
  type: string
  target?: string
  content?: Record<string, any>
  conditions?: Record<string, any>
  repeat?: {
    interval: number
    max_count?: number
  }
}

// 世界配置数据
export interface WorldConfigData {
  key: string
  name: string
  description?: string
  setting?: string
  era?: string
  atmosphere?: string
  rules?: string[]
  tags?: string[]
}

// 派系配置数据
export interface FactionConfigData {
  key: string
  name: string
  description?: string
  ideology?: string
  goals?: string[]
  relationships?: FactionRelationship[]
  base_attributes?: Record<string, number>
}

// 派系关系
export interface FactionRelationship {
  target_faction_key: string
  initial_value: number
  description?: string
}

// 地点配置数据
export interface LocationConfigData {
  key: string
  name: string
  description?: string
  location_type: string
  connections?: string[]
  properties?: Record<string, any>
  atmosphere?: string
  available_devices?: string[]
}

// 对话配置数据
export interface DialogueConfigData {
  key: string
  name: string
  description?: string
  participants: string[]
  start_conditions?: Record<string, any>
  dialogues: DialogueEntry[]
  end_conditions?: Record<string, any>
}

// AI模型配置数据
export interface AiModelConfigData {
  key: string
  name: string
  provider: string
  model_id: string
  temperature?: number
  max_tokens?: number
  system_prompt?: string
  capabilities?: string[]
  fallback_models?: string[]
}

// 资源图片配置数据（AI生成的图片资源描述）
export interface ResourceImageConfigData {
  key: string
  name: string
  resource_type: 'npc_portrait' | 'scene_background' | 'item_icon' | 'faction_logo' | 'other'
  target_id: string          // 关联的NPC/场景/物品ID
  target_type?: 'npc' | 'scene' | 'item' | 'faction'
  prompt: string             // AI绘画提示词
  style?: string             // 风格描述（如：电影质感、写实、卡通）
  resolution?: string        // 分辨率（如：2K, 512x512）
  description?: string       // 图片描述
  negative_prompt?: string   // 负面提示词（可选）
  tags?: string[]            // 标签
  reference_images?: string[] // 参考图片URL（可选）
}
